import {IObjectType, ITranslationParams} from "../index";
import {lm} from "../appContext";
import {Delimiter, ILanguageObject} from "../constants/Language";
import {ObjectTypeId} from "../constants";
import {getObjectDefinition} from "./misc";
import {storeObservable} from "./sessionstore";

export namespace nsLanguage {
    export function parseString(string: string, defaults?: Partial<ITranslationParams>): ITranslationParams {

        // Recursively replaces innermost pairs of double parentheses, `((` and `))`, and translates those until there
        // are no more parentheses left.
        // For example if input is
        // `go back to|destination:((manual user|p|suffix: [((search|ac))]|ac))`
        //                                                  ^^^1^^^^^^^^^
        //                         ^^^2^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
        //  ^^^3^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
        // then 1 would be translated/replaced first before translating/replacing 2, and finally 3.
        const regex = /\(\(((?:\"\(\(|\)\)\"|[^()])+)\)\)/
        if (regex.test(string)) {
            const ret = string.replace(regex, (whole: string, str: string) => {
                return lm.t(str, defaults ?? {}) ?? ''
            })
            return parseString(ret, defaults ?? {})
        }
        const str = string
        const segments = str.split(Delimiter) ?? [str]
        const key = segments.shift() ?? ''
        const dictionary: Record<string, string> = {}
        segments.forEach(str => {
            const pair = str.split(':')
            const dictKey = pair.shift()
            if (dictKey) {
                dictionary[dictKey] = pair.join(':') ?? ''
            }
        })
        const linebreak = 'br' in dictionary ? dictionary['br'] != 'false' : undefined
        const uppercase = 'uc' in dictionary ? dictionary['uc'] != 'false' : undefined
        const anticapitalize = 'ac' in dictionary ? dictionary['ac'] != 'false' : undefined
        const nocasing = 'nc' in dictionary ? dictionary['nc'] != 'false' : undefined
        const trailingdots = 'td' in dictionary ? dictionary['td'] != 'false' : undefined
        const capitalize = 'c' in dictionary ? dictionary['c'] != 'false' : undefined
        const obj = defaults && 'obj' in defaults ? defaults.obj: undefined
        const plural = 'p' in dictionary ? dictionary['p'] != 'false' : undefined
        const description = 'desc' in dictionary ? dictionary['desc'] != 'false' : undefined
        const number = 'n' in dictionary ? Number(dictionary['n']) : undefined
        const suffix = 'suffix' in dictionary ? dictionary['suffix'] : undefined
        const replace: any = {}
        Object.keys(dictionary).forEach((k: string) => {
            const nativeKeys: (keyof ITranslationParams)[] = ['ac', 'br', 'c', 'desc', 'k', 'n', 'p', 'r', 'suffix', 'td', 'uc'];
            if (nativeKeys.includes(k as any)) {
                return
            }
            replace[k] = dictionary[k]
        })
        return {
            br: linebreak,
            c: capitalize,
            desc: description,
            k: key,
            ac: anticapitalize,
            n: number,
            nc: nocasing,
            obj: obj,
            p: plural,
            r: replace,
            suffix: suffix,
            td: trailingdots,
            uc: uppercase,
            string: string,
        }
    }

    export function getNumObjectsLabel(num: number, obj: ObjectTypeId | IObjectType, manual: boolean): string {
        const ret: string[] = []
        if (!Number.isInteger(num)) {
            num = 0
        }
        ret.push(toLocaleString(num))
        if (manual) {
            ret.push(`((manual|ac|n:${num}))`)
        }
        const objDef = getObjectDefinition(obj);
        if (!objDef) {
            return ret.join(' ')
        }
        const objectName: string = objDef.id == ObjectTypeId.ApiUser
            ? objDef.singular
            : `${objDef.singular}|ac`
        ret.push(`((${objectName}|n:${num}))`)
        return ret.join(' ')
    }

    export function toLocaleString(num: number, options: Intl.NumberFormatOptions = {}): string {
        const lang = storeObservable('language')()
        return num.toLocaleString(lang.key, options)
    }

    export function getLanguageProperty(obj: ILanguageObject): string {
        return obj[lm.selectedLanguage().key] ?? obj['en'] ?? obj['nb'] ?? ''
    }

}
